import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '../ui-pages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const svg = (paths) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths}</svg>
);

const IconBot = svg(<>
  <rect x="3" y="8" width="18" height="12" rx="2" />
  <path d="M12 8V5" /><circle cx="12" cy="3.5" r="1.2" />
  <path d="M9 13h.01M15 13h.01" /><path d="M3 14H1M23 14h-2" />
</>);
const IconPlus = svg(<><path d="M12 5v14M5 12h14" /></>);
const IconEdit = svg(<><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>);
const IconTrash = svg(<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></>);
const IconLayers = svg(<><path d="M12 2 2 7l10 5 10-5-10-5Z" /><path d="m2 17 10 5 10-5" /><path d="m2 12 10 5 10-5" /></>);
const IconTag = svg(<><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z" /><path d="M7 7h.01" /></>);
const IconSearch = svg(<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>);
const IconClose = svg(<><path d="M18 6 6 18M6 6l12 12" /></>);
const IconAlert = svg(<><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></>);

const BotIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 8V4H8" /><rect width="16" height="12" x="4" y="8" rx="2" /><path d="M2 14h2" /><path d="M20 14h2" /><path d="M15 13v2" /><path d="M9 13v2" />
  </svg>
);

const AiAgents = () => {
  const { workspaceUuid } = useParams();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, agent = edit
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/ai-agents?workspaceUuid=${workspaceUuid}`);
      setAgents(r.data || []);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  }, [workspaceUuid]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    if (!window.confirm('Delete this AI agent?')) return;
    try {
      await axios.delete(`${API_URL}/api/ai-agents/${id}`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const q = query.trim().toLowerCase();
  const visible = q
    ? agents.filter(a =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.prompt || '').toLowerCase().includes(q) ||
        (a.fields || []).some(f => (f || '').toLowerCase().includes(q)))
    : agents;

  const totalFields = agents.reduce((n, a) => n + (a.fields || []).length, 0);

  return (
    <div className="ui-page">
      <div className="ui-head">
        <div>
          <h1 className="ui-title">AI Agents</h1>
          <p className="ui-sub">
            Create reusable AI conversation agents that can be added as blocks in your workflows.
          </p>
        </div>
        <div className="ui-head-actions">
          <div className="ui-search">
            {IconSearch}
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search agents…"
            />
          </div>
          <button className="ui-btn" onClick={() => setEditing({})}>
            {IconPlus} New AI Agent
          </button>
        </div>
      </div>

      <div className="ui-stats">
        <div className="ui-stat tone-violet">
          <span className="ui-stat-ic">{IconBot}</span>
          <div>
            <div className="ui-stat-n">{agents.length}</div>
            <div className="ui-stat-l">Total Agents</div>
          </div>
        </div>
        <div className="ui-stat tone-blue">
          <span className="ui-stat-ic">{IconTag}</span>
          <div>
            <div className="ui-stat-n">{totalFields}</div>
            <div className="ui-stat-l">Defined Fields</div>
          </div>
        </div>
        <div className="ui-stat tone-emerald">
          <span className="ui-stat-ic">{IconLayers}</span>
          <div>
            <div className="ui-stat-n">{q ? visible.length : agents.length}</div>
            <div className="ui-stat-l">{q ? 'Matching' : 'Reusable Blocks'}</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="ui-card" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#B23A3A', borderColor: '#E7E1D6', background: '#F6E4E1', marginBottom: 16 }}>
          {IconAlert} {error}
        </div>
      )}

      {loading ? (
        <div className="ui-grid cl-skel-stagger">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="cl-skel-card">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div className="cl-skel cl-skel-circle" style={{ width: 42, height: 42, flex: 'none' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="cl-skel" style={{ width: '55%', height: 15, borderRadius: 6 }} />
                  <div className="cl-skel" style={{ width: '40%', height: 10, borderRadius: 6, marginTop: 8 }} />
                </div>
              </div>
              <div style={{ marginTop: 14 }}>
                <div className="cl-skel" style={{ width: '95%', height: 10, borderRadius: 6 }} />
                <div className="cl-skel" style={{ width: '90%', height: 10, borderRadius: 6, marginTop: 8 }} />
                <div className="cl-skel" style={{ width: '70%', height: 10, borderRadius: 6, marginTop: 8 }} />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14 }}>
                <div className="cl-skel" style={{ width: 60, height: 22, borderRadius: 999 }} />
                <div className="cl-skel" style={{ width: 60, height: 22, borderRadius: 999 }} />
                <div className="cl-skel" style={{ width: 60, height: 22, borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="ui-empty">
          <div className="ui-empty-ic">{IconBot}</div>
          <h2>{q ? 'No matching agents' : 'No AI agents yet'}</h2>
          <p>
            {q
              ? 'Try a different search term.'
              : 'Create reusable AI conversation agents to drop into your workflows as blocks.'}
          </p>
          {!q && (
            <button className="ui-btn" onClick={() => setEditing({})}>
              {IconPlus} New AI Agent
            </button>
          )}
        </div>
      ) : (
        <div className="ui-grid">
          {visible.map((a, i) => (
            <div key={a._id} className="ui-card" style={{ animationDelay: `${Math.min(i * 40, 320)}ms` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div className="ui-avatar" style={{ backgroundColor: '#111111', color: '#F9F4EE', borderRadius: '999px', display: 'grid', placeItems: 'center' }} aria-label="AI agent">
                  <BotIcon />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, letterSpacing: '-0.03em', fontSize: 16, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.name}
                  </div>
                  <div style={{ color: '#9C978C', fontSize: 12, marginTop: 3, fontFamily: "'IBM Plex Mono', monospace" }}>
                    Updated {new Date(a.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
              </div>

              {a.prompt && (
                <p style={{
                  margin: '14px 0 0', color: '#6B6862', fontSize: 13, lineHeight: 1.5,
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {a.prompt}
                </p>
              )}

              {(a.fields || []).length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14 }}>
                  {(a.fields || []).map(f => (
                    <span key={f} className="ui-pill" style={{ color: '#111111', background: '#FAF7F1', fontFamily: "'IBM Plex Mono', monospace" }}>
                      {`{${f}}`}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 14, borderTop: '1px solid #E7E1D6' }}>
                <button className="ui-btn ui-btn-ghost ui-btn-sm" onClick={() => setEditing(a)}>
                  {IconEdit} Edit
                </button>
                <button className="ui-btn ui-btn-danger ui-btn-sm" onClick={() => remove(a._id)}>
                  {IconTrash} Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <AgentEditor agent={editing} workspaceUuid={workspaceUuid} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
};

const AgentEditor = ({ agent, workspaceUuid, onClose, onSaved }) => {
  const [name, setName] = useState(agent.name || '');
  const [prompt, setPrompt] = useState(agent.prompt || '');
  const [fieldsText, setFieldsText] = useState((agent.fields || []).join(', '));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const isEdit = !!agent._id;

  const save = async () => {
    setErr('');
    const fields = fieldsText.split(',').map(s => s.trim()).filter(Boolean);
    if (!name.trim() || !prompt.trim()) { setErr('Name and prompt are required.'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API_URL}/api/ai-agents/${agent._id}`, { name, prompt, fields });
      } else {
        await axios.post(`${API_URL}/api/ai-agents`, { workspaceUuid, name, prompt, fields });
      }
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    setSaving(false);
  };

  const labelStyle = { fontSize: 11, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '.06em' };
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 14, border: '1px solid #E7E1D6', fontSize: 14, marginBottom: 16, marginTop: 6, boxSizing: 'border-box', outline: 'none' };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.30)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#FFFFFF', borderRadius: 20, border: '1px solid #E7E1D6', padding: 24, width: 720, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '4px 4px 0 rgba(17,17,17,.2)', animation: 'uiIn .25s ease both' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="ui-ic tone-violet" style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {IconBot}
            </span>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em' }}>
              {isEdit ? 'Edit AI Agent' : 'New AI Agent'}
            </h3>
          </div>
          <button onClick={onClose} className="ui-btn ui-btn-ghost ui-btn-sm" style={{ padding: 8, borderRadius: 10 }} aria-label="Close">
            {IconClose}
          </button>
        </div>

        {err && (
          <div className="ui-card" style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#B23A3A', borderColor: '#E7E1D6', background: '#F6E4E1', marginBottom: 16, padding: '12px 14px' }}>
            {IconAlert} {err}
          </div>
        )}

        <label style={labelStyle}>Name</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Refund Collector" style={inputStyle} />

        <label style={labelStyle}>Fields (comma-separated)</label>
        <input value={fieldsText} onChange={e => setFieldsText(e.target.value)} placeholder="status, orderNo, reason" style={inputStyle} />

        <label style={labelStyle}>Prompt</label>
        <p style={{ fontSize: 12, color: '#6B6862', margin: '6px 0 8px', lineHeight: 1.5 }}>
          Must include <code>{'{chat_history}'}</code> and <code>{'{user_input}'}</code>.
          Reference fields like <code>{'{{orderNo}}'}</code> inside the prompt.
        </p>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={20}
          style={{ width: '100%', padding: 12, borderRadius: 14, border: '1px solid #E7E1D6', fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", boxSizing: 'border-box', marginBottom: 20, outline: 'none', lineHeight: 1.5 }} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} className="ui-btn ui-btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="ui-btn">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiAgents;
